import { Button } from '@/components/ui/Button/Button';
import { paths } from '@/config/paths';
import { useTimeFor } from '@/hooks/useTimeFor';
import { useErrorTranslation } from '@/hooks/useErrorTranslation';
import { useAuthStore } from '@/stores/authStore';
import { ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { useGetUpcomingAppointments } from '../../api/getUpcomingAppointments';
import { groupAppointmentsByTime } from '../../utils/groupAppointmentsByTime';
import { MultipleSessionsListItem } from '../MultipleSessionsListItem/MultipleSessionsListItem';
import { SessionOverviewEmpty } from '../SessionOverviewEmpty/SessionOverviewEmpty';
import { SessionOverviewError } from '../SessionOverviewError/SessionOverviewError';
import { SessionOverviewLoading } from '../SessionOverviewLoading/SessionOverviewLoading';
import { SingleSessionListItem } from '../SingleSessionListItem/SingleSessionListItem';

// The preview shows every session across the next 2 days that have sessions
// (not necessarily consecutive), so all sessions on a displayed day are visible.
const UPCOMING_DAYS_COUNT = 2;

export const UpcomingSessionsOverview = () => {
  const { t } = useTranslation('scheduling', { keyPrefix: 'upcomingSessionsOverview' });
  const { translateError } = useErrorTranslation();

  const { userData } = useAuthStore();
  const { timeZone: centerTimeZone } = useTimeFor('sessionTime');

  const students = userData?.students ?? [];
  const hasOnlyOneStudent = students.length === 1;

  const { data, isLoading, error } = useGetUpcomingAppointments({
    days: UPCOMING_DAYS_COUNT,
    timeZone: centerTimeZone,
  });

  const appointments = data?.data.appointments ?? [];
  const groupedAppointments = groupAppointmentsByTime(appointments, centerTimeZone);

  return (
    <section className='bg-fill-background-light flex flex-col gap-4 rounded-lg p-6' aria-busy={isLoading}>
      <Button asChild variant='link' className='self-start p-0'>
        <Link to={paths.scheduling.sessions.home.path} className='flex items-center gap-2'>
          <h2 className='text-text-default-secondary'>{t('title')}</h2>
          <ChevronRight className='text-text-default-secondary size-5' />
        </Link>
      </Button>

      {isLoading && <SessionOverviewLoading />}

      {!isLoading && error && <SessionOverviewError>{translateError(error)}</SessionOverviewError>}

      {!isLoading && !error && appointments.length === 0 && <SessionOverviewEmpty>{t('empty')}</SessionOverviewEmpty>}

      {!isLoading && !error && appointments.length > 0 && (
        <ul className='space-y-5'>
          {hasOnlyOneStudent
            ? appointments.map((appointment) => <SingleSessionListItem appointment={appointment} key={appointment.appointment_id} />)
            : groupedAppointments.map((sessionDate) => <MultipleSessionsListItem sessionDate={sessionDate} key={sessionDate.date.toISOString()} />)}
        </ul>
      )}
    </section>
  );
};
